import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSpaceStore } from '@/stores/spaceStore'
import { useTagsStore } from '@/stores/tagsStore'
import { SPACE_ICONS } from '@/lib/icons'
import Modal from '@/components/ui/Modal'
import { showToast } from '@/components/ui/Toast'
import { api } from '@/api/client'
import styles from './CreateSpaceModal.module.css'

interface Props {
  open: boolean
  onClose: () => void
}

const FALLBACK_SPACE_PRESETS = [
  { id: 'study', name: '学习', icon: '🎓', forType: 'team' as const, tags: [
    { name: '上课', icon: '👨‍🏫' }, { name: '自习', icon: '📖' }, { name: '笔记', icon: '📝' },
    { name: '考试', icon: '📋' }, { name: '作业', icon: '✏️' }, { name: '复习', icon: '🔄' },
    { name: '网课', icon: '💻' }, { name: '论文', icon: '📄' },
  ]},
  { id: 'fitness', name: '健身', icon: '💪', tags: [
    { name: '跑步', icon: '🏃' }, { name: '力量', icon: '🏋️' }, { name: '有氧', icon: '🚴' },
    { name: '瑜伽', icon: '🧘' }, { name: '拉伸', icon: '🤸' }, { name: '饮食', icon: '🥗' },
    { name: '睡眠', icon: '😴' }, { name: '体重', icon: '⚖️' },
  ]},
  { id: 'work', name: '工作', icon: '💼', forType: 'team' as const, tags: [
    { name: '会议', icon: '📅' }, { name: '任务', icon: '✅' }, { name: '汇报', icon: '📊' },
    { name: '出差', icon: '🚄' }, { name: '加班', icon: '🌙' }, { name: '灵感', icon: '💡' },
    { name: '邮件', icon: '📧' }, { name: '复盘', icon: '📝' },
  ]},
  { id: 'project', name: '项目', icon: '🚀', forType: 'team' as const, tags: [
    { name: '需求', icon: '📋' }, { name: '设计', icon: '🎨' }, { name: '开发', icon: '💻' },
    { name: '测试', icon: '🧪' }, { name: 'Bug', icon: '🐛' }, { name: '部署', icon: '🚢' },
    { name: '文档', icon: '📄' }, { name: '评审', icon: '👀' },
  ]},
  { id: 'reading', name: '读书', icon: '📖', tags: [
    { name: '在读', icon: '📖' }, { name: '已读', icon: '✅' }, { name: '书评', icon: '✍️' },
    { name: '摘抄', icon: '📝' }, { name: '推荐', icon: '⭐' }, { name: '借阅', icon: '📚' },
  ]},
  { id: 'travel', name: '旅行', icon: '✈️', tags: [
    { name: '行程', icon: '🗺️' }, { name: '交通', icon: '🚄' }, { name: '住宿', icon: '🏨' },
    { name: '美食', icon: '🍜' }, { name: '景点', icon: '📸' }, { name: '购物', icon: '🛍️' },
    { name: '花销', icon: '💰' }, { name: '签证', icon: '🛂' },
  ]},
  { id: 'life', name: '生活', icon: '🏠', tags: [
    { name: '做饭', icon: '🍳' }, { name: '打扫', icon: '🧹' }, { name: '购物', icon: '🛒' },
    { name: '维修', icon: '🔧' }, { name: '缴费', icon: '💰' }, { name: '取快递', icon: '📦' },
    { name: '遛狗', icon: '🐕' }, { name: '浇花', icon: '🌻' },
  ]},
  { id: 'food', name: '美食', icon: '🍜', tags: [
    { name: '探店', icon: '🏪' }, { name: '做饭', icon: '🍳' }, { name: '烘焙', icon: '🧁' },
    { name: '外卖', icon: '🛵' }, { name: '食谱', icon: '📋' }, { name: '打卡', icon: '📍' },
  ]},
  { id: 'movies', name: '影视', icon: '🎬', tags: [
    { name: '电影', icon: '🎥' }, { name: '电视剧', icon: '📺' }, { name: '综艺', icon: '🎭' },
    { name: '纪录片', icon: '🎞️' }, { name: '动漫', icon: '🎌' }, { name: '短片', icon: '📱' },
  ]},
  { id: 'music', name: '音乐', icon: '🎵', tags: [
    { name: '练琴', icon: '🎹' }, { name: '唱歌', icon: '🎤' }, { name: '听歌', icon: '🎧' },
    { name: '演出', icon: '🎭' }, { name: '扒谱', icon: '🎼' }, { name: '录音', icon: '🎙️' },
  ]},
  { id: 'photography', name: '摄影', icon: '📷', tags: [
    { name: '拍照', icon: '📸' }, { name: '修图', icon: '🎨' }, { name: 'Vlog', icon: '🎬' },
    { name: '风景', icon: '🌄' }, { name: '人像', icon: '👤' }, { name: '街拍', icon: '🏙️' },
  ]},
  { id: 'games', name: '游戏', icon: '🎮', tags: [
    { name: '手游', icon: '📱' }, { name: '主机', icon: '🎮' }, { name: '桌游', icon: '🎲' },
    { name: '密室', icon: '🔐' }, { name: '剧本杀', icon: '🎭' }, { name: '棋牌', icon: '♟️' },
  ]},
  { id: 'health', name: '健康', icon: '🏥', tags: [
    { name: '就医', icon: '💊' }, { name: '体检', icon: '🩺' }, { name: '用药', icon: '💉' },
    { name: '康复', icon: '🩹' }, { name: '心理', icon: '🧠' }, { name: '养生', icon: '🍵' },
  ]},
  { id: 'skills', name: '技能', icon: '🎯', tags: [
    { name: '编程', icon: '💻' }, { name: '设计', icon: '🎨' }, { name: '语言', icon: '🌍' },
    { name: '乐器', icon: '🎹' }, { name: '手工', icon: '✂️' }, { name: '绘画', icon: '🖌️' },
  ]},
  { id: 'bestie', name: '闺蜜', icon: '👩‍❤️‍👩', forType: 'team' as const, tags: [
    { name: '聚会', icon: '🥂' }, { name: '八卦', icon: '🗣️' }, { name: '美妆', icon: '💄' },
    { name: '逛街', icon: '🛍️' }, { name: '下午茶', icon: '☕' }, { name: '自拍', icon: '🤳' },
  ]},
  { id: 'friends', name: '朋友', icon: '🤝', forType: 'team' as const, tags: [
    { name: '聚餐', icon: '🍽️' }, { name: '运动', icon: '🏃' }, { name: '游戏', icon: '🎮' },
    { name: '出游', icon: '🚗' }, { name: '分享', icon: '💬' }, { name: '打卡', icon: '📍' },
  ]},
  { id: 'parenting', name: '育儿', icon: '👶', forType: 'team' as const, tags: [
    { name: '喂养', icon: '🍼' }, { name: '早教', icon: '📖' }, { name: '游戏', icon: '🧸' },
    { name: '辅食', icon: '🥣' }, { name: '体检', icon: '🏥' }, { name: '疫苗', icon: '💉' },
    { name: '成长', icon: '📏' },
  ]},
  { id: 'pet', name: '宠物', icon: '🐾', tags: [
    { name: '喂食', icon: '🦴' }, { name: '遛弯', icon: '🐕' }, { name: '洗澡', icon: '🛁' },
    { name: '体检', icon: '🏥' }, { name: '训练', icon: '🎾' }, { name: '美容', icon: '✂️' },
  ]},
  { id: 'home', name: '装修', icon: '🏗️', tags: [
    { name: '设计', icon: '🎨' }, { name: '选材', icon: '🪵' }, { name: '施工', icon: '🔨' },
    { name: '验收', icon: '✅' }, { name: '软装', icon: '🛋️' }, { name: '预算', icon: '💰' },
  ]},
  { id: 'garden', name: '园艺', icon: '🌿', tags: [
    { name: '播种', icon: '🌱' }, { name: '浇水', icon: '💧' }, { name: '施肥', icon: '🌾' },
    { name: '修剪', icon: '✂️' }, { name: '收获', icon: '🍅' }, { name: '病虫', icon: '🐛' },
  ]},
  { id: 'shopping', name: '购物', icon: '🛒', tags: [
    { name: '日用', icon: '🧴' }, { name: '服饰', icon: '👗' }, { name: '数码', icon: '📱' },
    { name: '美妆', icon: '💄' }, { name: '家居', icon: '🏠' }, { name: '比价', icon: '💰' },
  ]},
  { id: 'couple', name: '情侣', icon: '💕', forType: 'couple' as const, tags: [
    { name: '约会', icon: '💑' }, { name: '旅行', icon: '✈️' }, { name: '美食', icon: '🍜' },
    { name: '纪念日', icon: '🎂' }, { name: '日常', icon: '🏠' }, { name: '看电影', icon: '🎬' },
    { name: '散步', icon: '🚶' }, { name: '做饭', icon: '🍳' },
  ]},
  { id: 'newlywed', name: '新婚', icon: '💒', forType: 'couple' as const, tags: [
    { name: '蜜月', icon: '✈️' }, { name: '布置新家', icon: '🏠' }, { name: '见家长', icon: '👨‍👩‍👧' },
    { name: '纪念日', icon: '🎂' }, { name: '约会', icon: '💑' }, { name: '做饭', icon: '🍳' },
  ]},
  { id: 'longdistance', name: '异地恋', icon: '📱', forType: 'couple' as const, tags: [
    { name: '视频通话', icon: '📹' }, { name: '语音', icon: '🎤' }, { name: '写信', icon: '✉️' },
    { name: '寄礼物', icon: '🎁' }, { name: '倒计时', icon: '⏰' }, { name: '见面', icon: '🚄' },
  ]},
]

const FALLBACK_COUPLE_DEFAULT_TAGS = [
  { name: '约会', icon: '💑' }, { name: '旅行', icon: '✈️' }, { name: '美食', icon: '🍜' },
  { name: '纪念日', icon: '🎂' }, { name: '日常', icon: '🏠' }, { name: '看电影', icon: '🎬' },
  { name: '散步', icon: '🚶' }, { name: '做饭', icon: '🍳' }, { name: '逛公园', icon: '🌳' },
  { name: '礼物', icon: '🎁' }, { name: '运动', icon: '🏃' }, { name: '聊天', icon: '💬' },
]

interface ConfigResponse {
  spaceTemplates?: Array<{ id: string; name: string; icon: string; forType?: string; tags?: Array<{ name: string; icon: string }> }>
  coupleDefaultTags?: Array<{ name: string; icon: string }>
}

export default function CreateSpaceModal({ open, onClose }: Props) {
  const { createSpace } = useSpaceStore()
  const { addTag } = useTagsStore()
  const [name, setName] = useState('')
  const [type, setType] = useState<'couple' | 'team'>('couple')
  const [preset, setPreset] = useState<string | null>(null)
  const [icon, setIcon] = useState('💕')
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [customTag, setCustomTag] = useState('')
  const [periodEnabled, setPeriodEnabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const [spacePresets, setSpacePresets] = useState(FALLBACK_SPACE_PRESETS)
  const [coupleDefaultTags, setCoupleDefaultTags] = useState(FALLBACK_COUPLE_DEFAULT_TAGS)

  const RECENT_TEMPLATES_KEY = 'recentSpaceTemplates'
  const getRecentTemplates = (): string[] => {
    try { return JSON.parse(localStorage.getItem(RECENT_TEMPLATES_KEY) || '[]') } catch { return [] }
  }
  const trackRecentTemplate = (id: string) => {
    const recent = getRecentTemplates().filter(r => r !== id)
    recent.unshift(id)
    localStorage.setItem(RECENT_TEMPLATES_KEY, JSON.stringify(recent.slice(0, 10)))
  }

  const sortedPresets = useMemo(() => {
    const filtered = spacePresets.filter(p => !p.forType || p.forType === type)
    const recent = getRecentTemplates()
    if (!recent.length) return filtered
    return [...filtered].sort((a, b) => {
      const ai = recent.indexOf(a.id)
      const bi = recent.indexOf(b.id)
      if (ai === -1 && bi === -1) return 0
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  }, [spacePresets, type])

  const fetchConfig = useCallback(async () => {
    try {
      const d = await api.get<ConfigResponse>('/api/config')
      if (d.spaceTemplates && d.spaceTemplates.length > 0) {
        setSpacePresets(d.spaceTemplates.map(t => ({
          id: t.id,
          name: t.name,
          icon: t.icon,
          forType: t.forType as 'couple' | 'team' | undefined,
          tags: t.tags || [],
        })))
      }
      if (d.coupleDefaultTags && d.coupleDefaultTags.length > 0) {
        setCoupleDefaultTags(d.coupleDefaultTags)
      }
    } catch {
      // keep fallback
    }
  }, [])

  useEffect(() => {
    if (open) fetchConfig()
  }, [open, fetchConfig])

  const selectedPreset = spacePresets.find(p => p.id === preset)

  const handleTypeChange = (t: 'couple' | 'team') => {
    setType(t)
    setIcon(t === 'couple' ? '💕' : '👥')
    setPeriodEnabled(t === 'couple')
  }

  const handlePresetSelect = (id: string) => {
    if (preset === id) {
      setPreset(null)
      setIcon(type === 'couple' ? '💕' : '👥')
    } else {
      setPreset(id)
      const p = spacePresets.find(pr => pr.id === id)
      if (p) setIcon(p.icon)
    }
  }

  const handleSubmit = async () => {
    if (!name.trim()) { showToast('请输入空间名称'); return }
    setLoading(true)
    try {
      const newSpace = await createSpace(name.trim(), type, icon, periodEnabled)
      if (newSpace) {
        if (preset) trackRecentTemplate(preset)
        const tagsToAdd = selectedPreset?.tags || (type === 'couple' ? coupleDefaultTags : [])
        for (const t of tagsToAdd) {
          try { await addTag({ name: t.name, icon: t.icon }) } catch {}
        }
        if (customTag.trim()) {
          for (const t of customTag.split(/[,，、\s]+/).filter(Boolean)) {
            try { await addTag({ name: t.trim(), icon: '📌' }) } catch {}
          }
        }
      }
      showToast('空间已创建')
      setName('')
      setType('couple')
      setPreset(null)
      setIcon('💕')
      setPeriodEnabled(true)
      setCustomTag('')
      onClose()
    } catch (e) {
      showToast(e instanceof Error ? e.message : '创建失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="创建空间">
      <div className={styles.form}>
        <div className={styles.nameRow}>
          <span
            className={styles.spaceIcon}
            onClick={() => setShowIconPicker(!showIconPicker)}
            title="点击选择图标"
          >{icon}</span>
          <input
            className={styles.input}
            placeholder="空间名称"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && name.trim()) handleSubmit() }}
          />
        </div>
        {showIconPicker && (
          <div className={styles.iconPicker}>
            {SPACE_ICONS.map(em => (
              <button
                key={em}
                className={`${styles.iconBtn} ${icon === em ? styles.iconBtnActive : ''}`}
                onClick={() => { setIcon(em); setShowIconPicker(false) }}
              >{em}</button>
            ))}
          </div>
        )}
        <div className={styles.typeGroup}>
          <button
            className={type === 'couple' ? styles.typeBtnActive : styles.typeBtn}
            onClick={() => handleTypeChange('couple')}
          >
            💕 情侣
          </button>
          <button
            className={type === 'team' ? styles.typeBtnActive : styles.typeBtn}
            onClick={() => handleTypeChange('team')}
          >
            👥 团队
          </button>
        </div>
        {type === 'couple' && (
          <div className={styles.hint}>💕 情侣空间限 2 人，创建后可通过邀请系统邀请伴侣加入</div>
        )}

        <div className={styles.presetSection}>
          <div className={styles.presetLabel}>选择模板（可选）</div>
          <div className={styles.presetGrid}>
            {sortedPresets.map(p => (
              <button
                key={p.id}
                className={`${styles.presetBtn} ${preset === p.id ? styles.presetBtnActive : ''}`}
                onClick={() => handlePresetSelect(p.id)}
              >
                <span className={styles.presetBtnIcon}>{p.icon}</span>
                <span className={styles.presetBtnName}>{p.name}</span>
              </button>
            ))}
          </div>
        </div>

        {selectedPreset && selectedPreset.tags.length > 0 && (
          <div className={styles.presetPreview}>
            <div className={styles.presetPreviewLabel}>预览标签：</div>
            <div className={styles.presetPreviewTags}>
              {selectedPreset.tags.map((t, i) => (
                <span key={i} className={styles.presetPreviewTag}>{t.icon} {t.name}</span>
              ))}
            </div>
          </div>
        )}

        <button
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? '创建中...' : '创建'}
        </button>
      </div>
    </Modal>
  )
}
